<template>
  <!-- 줄 전체가 회원 상세 링크 (Enter·가운데 클릭·새 탭 열기 모두 링크 동작). 전화번호·특이사항을 끌어 고를 수 있게 링크 끌기는 끄고,
       이 줄에서 끌어 고른 직후의 누름은 옮기지 않는다(신규회원 관리와 같음, 5차 리뷰: 줄 전체가 끌리는 링크라 글자를 고르거나 복사할 수 없었다).
       href(새 탭·가운데 클릭·링크 복사)에는 보던 목록 주소를 from에 싣는다: 새 탭에는 방문 기록이 없어 사이드메뉴 "회원관리"가 필터·쪽을
       잃었다(6차 리뷰, 신규회원 관리 openProfile과 같음). 같은 탭 이동(navigate)은 from 없는 주소다 -->
  <router-link v-slot="{ navigate }" :to="profilePath" custom>
    <a
      :href="newTabHref"
      class="row"
      draggable="false"
      @mousedown="onRowMousedown"
      @click="onRowClick($event, navigate)"
    >
      <div class="cell who">
        <div class="name">{{ member.name }}</div>
        <div class="phone">{{ phoneDisplay }}</div>
      </div>

      <!-- 넓은 화면에서는 .label이 화면에 안 보이고 읽기 도구에만 읽힌다(머리글이 대신 보임) -->
      <div class="cell">
        <span class="label">수강권</span>
        <div class="value">
          <div class="line" :title="membershipDisplay">
            {{ membershipDisplay }}
            <span v-if="otherMembershipCount > 0" class="count">
              외 {{ otherMembershipCount }}개
            </span>
          </div>
          <div v-if="currentStartText" class="line sub info">
            {{ currentStartText }}
          </div>
          <div v-if="upcomingText" class="line sub info" :title="upcomingText">
            {{ upcomingText }}
          </div>
        </div>
      </div>

      <div class="cell">
        <span class="label">남은 횟수</span>
        <div class="value">
          <div class="line" :class="{ danger: isDangerousRemaining }">
            {{ remainingClassesDisplay }}
          </div>
        </div>
      </div>

      <div class="cell">
        <span class="label">만료일</span>
        <div class="value">
          <div class="line" :class="{ danger: isExpired || isExpiringSoon }">
            {{ expiryDateDisplay }}
          </div>
          <div
            v-if="daysRemainingText"
            class="line sub days"
            :class="daysRemainingClass"
          >
            {{ daysRemainingText }}
          </div>
        </div>
      </div>

      <div class="cell">
        <span class="label">최근 출석</span>
        <div class="value">
          <div class="line">{{ lastAttendanceDisplay }}</div>
        </div>
      </div>

      <div class="cell">
        <span class="label">특이사항</span>
        <div class="value">
          <div
            class="line note"
            :class="{ empty: !adminNote }"
            :title="adminNote || null"
          >
            {{ adminNote || "—" }}
          </div>
          <!-- "최근 수정" 정렬에서만 서버가 보낸다: 특이사항·메모 중 무엇이 언제 마지막으로 바뀌었는지 -->
          <div v-if="noteUpdatedText" class="line sub changed">
            {{ noteUpdatedText }}
          </div>
        </div>
      </div>
    </a>
  </router-link>
</template>

<script>
import {
  daysLeftText,
  daysLeftTone,
  dotDate,
  membershipStateLabel,
  noteUpdatedText,
  prettyPhone,
  shortDate,
} from "@/lib/memberNotes";

export default {
  name: "MemberRow",
  props: {
    member: { type: Object, required: true },
  },
  data() {
    return {
      pressedAt: null, // 이 줄에서 왼쪽 버튼을 누른 자리 { x, y }. 끌어 고르기(움직인 누름)와 그냥 누름을 가른다
    };
  },
  computed: {
    profilePath() {
      return `/profile/${encodeURIComponent(this.member.user_id || "")}`;
    },
    newTabHref() {
      return this.$router.resolve({
        path: this.profilePath,
        query: { from: this.$route.fullPath },
      }).href;
    },
    phoneDisplay() {
      return prettyPhone(this.member.phone_number) || "—";
    },
    activeMembershipCount() {
      return this.member.active_membership_count || 0;
    },
    hasActiveMembership() {
      return this.activeMembershipCount > 0;
    },
    // 새 응답 칸(memberships[]·admin_note)이 없는 옛 응답이면 빈 값으로 본다
    memberships() {
      return Array.isArray(this.member.memberships)
        ? this.member.memberships
        : [];
    },
    adminNote() {
      return String(this.member.admin_note || "").trim();
    },
    noteUpdatedText() {
      return noteUpdatedText(this.member);
    },
    // 지금 줄(가장 빨리 만료되는 활성 수강권)을 memberships[]에서 찾는다: 종류 + 만료일이 같은 첫 항목
    currentMembership() {
      if (!this.hasActiveMembership) return null;
      const type = this.member.membership_type;
      const expiry = this.member.expiry_date;
      return (
        this.memberships.find(
          (m) =>
            m.type === type &&
            String(m.expired_at || "").slice(0, 10) === expiry
        ) || null
      );
    },
    // 지금 줄 수강권이 아직 시작 전이면 "시작 예정 11.01"(신규회원 관리 배지와 같은 순서, 2차 리뷰)
    currentStartText() {
      const cur = this.currentMembership;
      if (!cur || cur.state !== "upcoming") return "";
      return `${membershipStateLabel("upcoming")} ${shortDate(cur.start_date)}`;
    },
    // 지금 줄 말고 시작 예정 수강권 (시작 빠른 순)
    upcomingOthers() {
      if (!this.hasActiveMembership) return [];
      return this.memberships
        .filter((m) => m.state === "upcoming" && m !== this.currentMembership)
        .sort((a, b) =>
          String(a.start_date || "").localeCompare(String(b.start_date || ""))
        );
    },
    // 지금 줄과 시작 예정 줄 말고 남은 활성 수강권 수(같이 쓰는 것, 날짜는 지났지만 활성인 것). 예전에는 활성 수강권 전체 수를 종류 바로
    // 뒤에 "Unlimited · 2개"로 붙여 Unlimited 두 개로 읽혔다(3차 리뷰). 시작 예정 수강권은 아래 줄("+ 10회권 10.26 시작 외 n개")이 센다
    otherMembershipCount() {
      if (!this.hasActiveMembership) return 0;
      return Math.max(
        0,
        this.activeMembershipCount - 1 - this.upcomingOthers.length
      );
    },
    // 지금 줄 말고 시작 예정 수강권: 가장 먼저 시작하는 것 하나 + 나머지 개수
    upcomingText() {
      const upcoming = this.upcomingOthers;
      if (upcoming.length === 0) return "";
      const first = upcoming[0];
      const more = upcoming.length > 1 ? ` 외 ${upcoming.length - 1}개` : "";
      return `+ ${first.type} ${shortDate(first.start_date)} 시작${more}`;
    },
    isExpired() {
      if (!this.hasActiveMembership) return false;
      if (!this.member.expiry_date || this.member.expiry_date === "-")
        return false;
      return this.member.days_until_expiry < 0;
    },
    // 만료일 빨강은 아래 "n일 남음"과 같은 규칙(lib/memberNotes daysLeftTone, 7일 미만). 예전에는 ≤ 7이라 7일 남은 수강권이 빨간 날짜
    // 아래 노란 "7일 남음"이었다(6차 리뷰)
    isExpiringSoon() {
      if (!this.hasActiveMembership) return false;
      if (!this.member.expiry_date || this.member.expiry_date === "-")
        return false;
      return daysLeftTone(this.member.days_until_expiry) === "danger";
    },
    isDangerousRemaining() {
      if (!this.hasActiveMembership) return false;
      const remaining = this.member.remaining_classes;
      if (
        !remaining ||
        remaining === "—" ||
        remaining === "-" ||
        remaining === "무제한"
      ) {
        return false;
      }
      const match = remaining.match(/^(\d+)\//);
      if (match) {
        const count = parseInt(match[1], 10);
        return count <= 2;
      }
      return false;
    },
    membershipDisplay() {
      if (!this.hasActiveMembership) return "—";
      const type = this.member.membership_type;
      return type && type !== "-" ? type : "—";
    },
    // 남은 횟수 "5/10" → "5/10회"(신규회원 관리·회원 상세와 같은 단위, 2차 리뷰). 무제한 등은 그대로
    remainingClassesDisplay() {
      if (!this.hasActiveMembership) return "—";
      const remaining = this.member.remaining_classes;
      if (!remaining || remaining === "-") return "—";
      return /^\d+\/\d+$/.test(remaining) ? `${remaining}회` : remaining;
    },
    expiryDateDisplay() {
      if (!this.hasActiveMembership) return "—";
      return dotDate(this.member.expiry_date) || "—";
    },
    lastAttendanceDisplay() {
      return dotDate(this.member.last_attendance) || "—";
    },
    // 남은 일자는 만료일 칸 둘째 줄에 붙는다 (수강권이 없으면 줄 자체를 그리지 않음)
    daysRemainingText() {
      if (!this.hasActiveMembership) return "";
      if (!this.member.expiry_date || this.member.expiry_date === "-") {
        return "";
      }

      // 회원 상세 수강권 카드와 같은 문구(lib/memberNotes daysLeftText, 4차 리뷰)
      return daysLeftText(this.member.days_until_expiry);
    },
    daysRemainingClass() {
      if (!this.hasActiveMembership) return "";
      if (!this.member.expiry_date || this.member.expiry_date === "-") {
        return "";
      }

      // 만료 회색, 1주 미만 빨강, 2주 미만 노랑, 그 밖 초록(회원 상세와 같음)
      return daysLeftTone(this.member.days_until_expiry);
    },
  },
  methods: {
    onRowMousedown(event) {
      this.pressedAt =
        event.button === 0 ? { x: event.clientX, y: event.clientY } : null;
    },
    // 이 줄에서 끌어 고른 직후(누른 자리에서 4px 넘게 움직였고 고른 글자가 있음)의 누름은 옮기지 않는다(전화번호 복사 등,
    // NewMembersView.openProfile과 같음). 그 밖은 링크 동작 그대로: Enter(detail 0)·그냥 누름은 옮기고, Ctrl/⌘·Shift 클릭은 navigate가
    // 브라우저에 맡겨 새 탭·새 창으로 연다. 예전에는 화면 어디든 고른 글자가 남아 있으면 줄의 여백·칸 사이 누름과 Enter가 아무 일도
    // 하지 않았다(6차 리뷰: 링크 여백을 눌러도 고른 글자가 풀리지 않는다)
    onRowClick(event, navigate) {
      const from = this.pressedAt;
      this.pressedAt = null;
      if (event.detail !== 0 && from) {
        const moved =
          Math.abs(event.clientX - from.x) > 4 ||
          Math.abs(event.clientY - from.y) > 4;
        const selected = window.getSelection
          ? String(window.getSelection())
          : "";
        if (moved && selected) {
          event.preventDefault();
          return;
        }
      }
      navigate(event);
    },
  },
};
</script>

<style scoped>
/* 열 너비·간격은 MemberListView의 .table에서 변수로 받아 머리글 줄과 맞춘다 */
.row {
  position: relative;
  display: grid;
  grid-template-columns: var(
    --member-cols,
    minmax(112px, 1.3fr) minmax(132px, 1.4fr) 76px 104px 96px minmax(0, 1.5fr)
  );
  align-items: center;
  gap: var(--member-gap, 16px);
  background: #0a0a0a;
  border: 1px solid transparent;
  border-radius: 10px;
  padding: 12px var(--member-pad-x, 20px);
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  color: #fff;
  text-decoration: none;
  cursor: pointer;
  -webkit-user-drag: none; /* 끌면 링크가 아니라 글자를 고른다 */
  transition: border-color 0.2s ease, background 0.2s ease;
}

.row:hover {
  background: #101213;
  border-color: #2a2a2a;
}

.row:focus-visible {
  outline: 2px solid #78ff47;
  outline-offset: 2px;
}

.cell {
  display: flex;
  flex-direction: column;
  min-width: 0;
  /* Chromium은 링크(줄) 위에서는 끌어 고르기를 시작하지 않고, 안쪽이 user-select: text일 때만 시작한다(5차 리뷰) */
  user-select: text;
}

/* 이름 + 전화번호 */
.who {
  gap: 2px;
}

.name {
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.2s ease;
}

.row:hover .name {
  color: #78ff47;
}

.phone {
  color: #9d9d9d;
  font-size: 14px;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 넓은 화면: 라벨은 머리글이 대신하므로 읽기 도구에만 남긴다 */
.label {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

.value {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.line {
  color: #fff;
  font-size: 15px;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.line.sub {
  font-size: 13px;
}

.count {
  color: #8e8e8e;
  font-size: 13px;
}

.line.info {
  color: #60a5fa; /* 시작 예정: 신규회원 배지·회원 상세 수강권 상태와 같은 info 파랑 (lib/memberNotes.js MEMBERSHIP_STATES) */
}

.line.note {
  color: #d8d8d8;
}

.line.note.empty {
  color: #6b7280;
}

.line.changed {
  color: #8e8e8e;
}

.line.danger {
  color: #ef4444;
}

.line.warning {
  color: #fbbf24; /* 노란색 */
}

.line.safe {
  color: #22c55e; /* 초록색 */
}

.line.expired {
  color: #6b7280; /* 회색 */
}

/* 남은 일자 강조 */
.line.days {
  font-weight: 600;
}

/* 모바일: 줄을 카드로 쌓고 칸마다 "라벨: 값" */
@media (max-width: 860px) {
  .row {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    padding: 14px 16px;
  }

  .who {
    flex-direction: row;
    align-items: baseline;
    flex-wrap: wrap;
    column-gap: 10px;
    padding-bottom: 10px;
    border-bottom: 1px solid #1a1a1a;
  }

  .cell:not(.who) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    padding: 8px 0;
    border-bottom: 1px solid #1a1a1a;
  }

  .cell:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .label {
    position: static;
    width: auto;
    height: auto;
    margin: 0;
    overflow: visible;
    clip: auto;
    flex-shrink: 0;
    color: #8e8e8e;
    font-size: 14px;
    line-height: 1.4;
  }

  .value {
    flex: 1;
    align-items: flex-end;
    text-align: right;
  }

  .line {
    max-width: 100%;
  }

  /* 카드에서는 마우스 올림(title)이 없으므로 특이사항을 세 줄까지 보인다(신규회원 카드와 같은 규칙) */
  .line.note {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    white-space: pre-wrap;
    word-break: keep-all;
    overflow-wrap: anywhere;
    text-align: left;
  }
}
</style>
